// The account menu controls the same notification destination as Settings.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import {
  type AppNoticeHost,
  appNoticeHost,
  DesktopNotificationQuickSettings,
  desktopShell,
  NotificationQuickSetting,
} from "@trameria/ui-core";
import { useMemo } from "react";
import {
  appNoticesQuery,
  browserPushQuery,
  usePushState,
  useSetApp,
  useSubscribeBrowser,
  useUnsubscribeBrowser,
} from "./queries";

export function PushQuickSettings() {
  const app = useMemo(() => appNoticeHost(), []);
  if (app) return <AppQuickSettings host={app} />;
  if (desktopShell()) return <DesktopNotificationQuickSettings />;
  return <BrowserQuickSettings />;
}

function BrowserQuickSettings() {
  const push = usePushState();
  const browser = useQuery(browserPushQuery());
  const subscribe = useSubscribeBrowser();
  const unsubscribe = useUnsubscribeBrowser();
  const endpoint =
    browser.data?.kind === "subscribed" ? browser.data.endpoint : null;
  const on = Boolean(
    endpoint &&
      push.data?.subscriptions.some(
        (s) => s.kind === "webpush" && s.endpoint === endpoint,
      ),
  );
  const publicKey = push.data?.publicKey;
  const reason =
    browser.data?.kind === "denied"
      ? m.push_blocked()
      : browser.data?.kind === "unsupported"
        ? browser.data.ios
          ? m.push_ios_add_to_home()
          : m.push_unsupported()
        : push.data && !publicKey
          ? m.push_unavailable()
          : null;
  return (
    <NotificationQuickSetting
      checked={on}
      disabled={
        !push.data ||
        !browser.data ||
        !!reason ||
        subscribe.isPending ||
        unsubscribe.isPending
      }
      onChange={(next) => {
        if (!next) unsubscribe.mutate();
        else if (publicKey) subscribe.mutate(publicKey);
      }}
      note={
        subscribe.isError
          ? m.push_subscribe_failed()
          : unsubscribe.isError
            ? m.push_unsubscribe_failed()
            : push.isError || browser.isError
              ? m.web_load_failed()
              : reason
      }
    />
  );
}

function AppQuickSettings({ host }: { host: AppNoticeHost }) {
  const push = usePushState();
  const state = useQuery(appNoticesQuery(host));
  const set = useSetApp(host);
  const on =
    push.data?.subscriptions.some(
      (s) => s.kind === "app" && s.id === host.notices.installationId,
    ) ?? false;
  return (
    <NotificationQuickSetting
      checked={on && state.data?.status === "authorized"}
      disabled={
        !push.data ||
        !state.data ||
        push.data.appPush === false ||
        set.isPending
      }
      onChange={(next) => set.mutate(next)}
      note={
        set.isError
          ? m.push_subscribe_failed()
          : push.isError || state.isError
            ? m.web_load_failed()
            : null
      }
    />
  );
}
